import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import './style.scss';
import SelectableListItem from 'components/Utils/SelectableListItem';
import List from '@material-ui/core/List';
import ListBadge from 'components/Common/ListBadge';
import CalendarUtils from 'services/CalendarUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import TimeZoneAgnosticBigCalendar, {
  CalendarMonthToolbar,
  convertDateToDateTime,
} from 'components/Schedule/TimeZoneAgnosticBigCalendar';
import Event from 'components/Schedule/Events';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import _ from 'lodash';
import StackItemsView from 'components/Common/StackItemsView';
import Icon from 'components/Utils/NewIcon';
import StaffUtils, { DEFAULT_VACATION_DAYS, StaffBadge, PERMISSIONS } from 'services/StaffUtils';
import { DefaultCheckbox } from 'services/UIUtils';
import ProgressMessage from 'components/Utils/ProgressMessage';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import MenuItem from '@material-ui/core/MenuItem';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import ShiftUtils from 'services/ShiftUtils';
import ResponsiveColumns from 'components/ResponsiveColumns';
import ListItem from '@material-ui/core/ListItem';
import { EditHandovers } from 'components/Schedule/Vacations/Handovers';
import { IsAdmin } from 'careand-redux/selectors';
import PhysicianUtils from 'services/PhysicianUtils';
import AppointmentUtils from 'services/AppointmentUtils';
import { TIME_OFF_REASONS } from 'components/Physician/BookTimeOff';
import ConfirmationButton from '../../Utils/NewDefaultButton/ConfirmationButton';

const DEFAULT_SHIFT = {
  non_working: true,
  approved: false,
  non_working_reason: 'Vacation',
};

const SHIFT_REQUEST_STATUS = {
  approved: 'Approved',
  partially_approved: 'Partially Approved',
  pending: 'Pending',
  declined: 'Declined',
};

const DEFAULT_REQUEST = {
  shifts: [],
  comments: '',
  status: SHIFT_REQUEST_STATUS.pending,
};

const EXISTING_FORMAT = 'YYYYMMDD';

export const NON_WORKING_MAP = {
  vacation: 'Vacation',
  stat: 'STAT',
  sick: 'Sick',
  emergency: 'Emergency',
  education: 'Education',
  bereavement: 'Bereavement',
  approved: 'Approved',
};

const SHIFT_TYPES = [
  {
    label: 'Paid Time Off',
    value: {
      non_working_reason: 'Vacation',
      non_working: true,
    },
  },
  {
    label: 'Half Paid Time Off (1st half off)',
    value: {
      non_working_reason: 'Vacation',
      half_day: '1',
      non_working: true,
    },
  },
  {
    label: 'Half Paid Time Off (2nd half off)',
    value: {
      non_working_reason: 'Vacation',
      half_day: '2',
      non_working: true,
    },
  },
  {
    label: 'Working 8AM-4PM',
    value: {
      non_working: false,
      startHour: '0800',
      breaks: [{ name: 'Lunch', start_hour: '1130', end_hour: '1230' }],
    },
  },
  {
    label: 'Working 9AM-5PM',
    value: {
      non_working: false,
      startHour: '0900',
      breaks: [{ name: 'Lunch', start_hour: '1230', end_hour: '1330' }],
    },
  },
  {
    label: 'Working 10AM-6PM',
    value: {
      non_working: false,
      startHour: '1000',
      breaks: [{ name: 'Lunch', start_hour: '1300', end_hour: '1400' }],
    },
  },
];

const Vacations = ({
  userId,
  userRole = 'physician',
  approveMode = false,
  onClose,
  showCalendar = true,
  setShowShifts,
  onClickedItem,
  showFullStats = false,
}) => {
  const dispatch = useAppDispatch();

  const shiftsReducer = useAppSelector((state) => state.shifts);
  const shifts = shiftsReducer.shifts;
  const requests = shiftsReducer.requests;
  const physicians = useAppSelector((state) => state.physicians.byId);
  const permissions = useAppSelector((state) => state.authentication.permissions);
  const ownId = useAppSelector((state) => state.authentication.user.id);
  const admins = useAppSelector((state) => state.admins.byId);
  const clinic = useAppSelector((state) => state.resource.clinic);

  const isAdmin = useAppSelector(IsAdmin);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [date, setDate] = useState(moment().unix());
  const [newRequest, setNewRequest] = useState(DEFAULT_REQUEST);
  const [handovers, setHandovers] = useState(false);
  const [stats, setStats] = useState([]);
  const [nonWorkingReason, setNonWorkingReason] = useState();
  const [blockedAppointments, setBlockedAppointments] = useState();
  const [yearIndex, setYearIndex] = useState(0);
  const [shiftType, setShiftType] = useState(undefined);
  const user = physicians[userId] || admins[userId];
  const navigateTo = useRef();

  function navigateToColumn(col) {
    setTimeout(() => {
      navigateTo.current?.(col);
    });
  }

  useEffect(() => {
    dispatch(api.Shifts.getShiftRequests({ userId }));
  }, [dispatch, userId]);

  useEffect(() => {
    getStats(userId);
    getStats(userId, 1);
  }, [dispatch, userId]);

  useEffect(() => {
    if (!!setShowShifts && selectedRequest) {
      setShowShifts(selectedRequest.shifts || []);
    }
  }, [selectedRequest]);

  async function getStats(userId, index = 0) {
    dispatch(api.Shifts.getShiftStats({ userId, yearIndex: index })).then((stats) => {
      if (stats) {
        setStats((prevState) => {
          let array = [...prevState];
          array[index] = stats;
          return array;
        });
      }
    });
  }

  const filteredReqs = useMemo(() => {
    return PerformanceUtils.sortBy(
      requests.filter((request) => {
        if (userId) return request.user_id === userId;
        return true;
      }),
      ['date_requested'],
    );
  }, [userId, requests]);

  function countDaysTaken(shifts = [], yearUnix) {
    if (yearUnix) {
      const yearString = moment.unix(yearUnix).format('YYYY');
      shifts = shifts.filter(({ start }) => yearString === moment.unix(start).format('YYYY'));
    }

    return shifts
      .filter((shift) => shift.non_working)
      .map((shift) => (shift.half_day ? 0.5 : 1))
      .reduce((accumulator, current) => accumulator + current, 0);
  }

  const completeStats = useMemo(() => {
    if (stats?.length > 0) {
      let stat = stats[yearIndex] || {};
      let taken = stat.vacation;
      if (taken === undefined) return {};
      let pending = countDaysTaken(
        _.flatten(filteredReqs.filter((req) => req.status === 'Pending').map((req) => req.shifts)),
      );
      let selected = countDaysTaken(newRequest.shifts);
      let available = (user.vacation_days || DEFAULT_VACATION_DAYS) - taken - pending - selected;
      return { ...stat, pending, available, ok: true };
    }
    return {};
  }, [stats, filteredReqs, newRequest, user.vacation_days, yearIndex]);

  const completeYears = useMemo(() => {
    return stats.map((stat = {}, index) => {
      let yearUnix = moment().add(index, 'year').unix();
      let taken = stat.vacation;
      if (taken === undefined) return {};
      let pending = countDaysTaken(
        _.flatten(filteredReqs.filter((req) => req.status === 'Pending').map((req) => req.shifts)),
        yearUnix,
      );
      let selected = countDaysTaken(newRequest.shifts, yearUnix);
      let available = (user.vacation_days || DEFAULT_VACATION_DAYS) - taken - pending - selected;
      return {
        ...stat,
        pending,
        available,
        ok: true,
        year: moment.unix(yearUnix).format('YYYY'),
      };
    });
  }, [stats, filteredReqs, newRequest, user.vacation_days]);

  let existingRequestedShifts = useMemo(() => {
    return _.flatten(
      filteredReqs
        .filter((request) => request.status === SHIFT_REQUEST_STATUS.pending)
        .map(({ shifts }) => shifts),
    ).filter(({ rejected }) => !rejected);
  }, [filteredReqs]);

  const extraShifts = useMemo(() => {
    const requestShifts = selectedRequest ? selectedRequest.shifts || [] : existingRequestedShifts;
    return [...(newRequest.shifts || []), ...requestShifts];
  }, [newRequest, selectedRequest, existingRequestedShifts]);

  function openHandovers() {
    setHandovers(true);
    setNewOpen(false);
    setSelectedRequest(undefined);
    navigateToColumn(1);
  }

  function startNewRequest() {
    setNewOpen(true);
    setHandovers(false);
    setSelectedRequest(undefined);
    setNewRequest(DEFAULT_REQUEST);
    setNonWorkingReason();
    navigateToColumn(1);
  }

  async function changeStatsYear(index) {
    setYearIndex(index);
    await getStats(userId, index);
  }

  function onSelectSlot({ start, end, slots }) {
    if (slots.length === 0) return;

    let totalCount = [0, 0];

    const yearIndex = {
      [moment().format('YYYY')]: 0,
      [moment().add(1, 'year').format('YYYY')]: 1,
    };
    let shifts = slots
      .map((slot) => {
        let index = yearIndex[slot.format('YYYY')];

        let available = completeYears[index]?.available || 0;
        let existingDates = existingRequestedShifts.map((slot) =>
          moment.unix(slot).format(EXISTING_FORMAT),
        );

        totalCount[index] += shiftType?.half_day ? 0.5 : 1;

        if (shiftType && (!shiftType.non_working || totalCount[index] <= available)) {
          slot = slot.add(10, 'hour');
          let startHour = shiftType?.startHour || '0800';
          let start = CalendarUtils.militaryTimeToUnix(slot.unix(), startHour);
          if (!existingDates.includes(moment.unix(start).format(EXISTING_FORMAT))) {
            let end = moment
              .unix(start)
              .add(shiftType?.half_day ? 4 : 8, 'hour')
              .unix();
            return {
              ...DEFAULT_SHIFT,
              user_role: userRole,
              start,
              end,
              clinic_id: user.clinic_id,
              ...shiftType,
              startHour: undefined,
            };
          }
        }
        return undefined;
      })
      .filter((slot) => !!slot);

    setNewRequest((prevState) => {
      let uniqShifts = _.uniqBy([...prevState.shifts, ...shifts], 'start');

      let newShifts = PerformanceUtils.sortBy(uniqShifts, ['start'], false);

      return {
        ...prevState,
        shifts: newShifts,
      };
    });
  }

  let className = 'vacations row full-height';
  if (newOpen && !selectedRequest) className += ' new-open';

  const statsElement = useMemo(() => {
    if (!completeStats.ok) return;
    const blockedAppointmentsCount = {};
    completeStats.blocked_appointments?.forEach((appt) => {
      let complaint = appt.complaints;
      if (TIME_OFF_REASONS.some((str) => complaint.includes(str + ' - ') || complaint === str))
        complaint = complaint.split(' - ')[0];
      else complaint = 'Other';
      blockedAppointmentsCount[complaint] = [...(blockedAppointmentsCount[complaint] || []), appt];
    });
    return (
      <div className="stats">
        {showFullStats && (
          <>
            {Object.keys(NON_WORKING_MAP).map((key) => {
              return (
                <span
                  key={key}
                  className="row"
                  onClick={() => {
                    setSelectedRequest(undefined);
                    setNewOpen(false);
                    setNonWorkingReason(key);
                    navigateToColumn(1);
                  }}
                >
                  {NON_WORKING_MAP[key]} Days:&nbsp;<b>{completeStats[key]}</b>
                  <Icon src="eye" />
                </span>
              );
            })}
            <br />
          </>
        )}
        {completeYears.map(
          (yearData, index) =>
            (!showFullStats || yearIndex === index) && (
              <div className="row" key={yearData.year}>
                {!showFullStats && (
                  <span>
                    <b>{yearData.year}</b>
                  </span>
                )}
                <span>
                  Paid Time Off Taken:&nbsp;<b>{yearData.vacation}</b>
                </span>
                <span>
                  Paid Time Off Pending:&nbsp;<b>{yearData.pending}</b>
                </span>
                <span>
                  Paid Time Off Available:&nbsp;<b>{yearData.available}</b>
                </span>
                <span>
                  Sick Days Taken:&nbsp;<b>{yearData.sick}</b>
                </span>
              </div>
            ),
        )}
        {showFullStats && Object.keys(blockedAppointmentsCount).length > 0 && (
          <>
            <br />
            <span className="small-title">Blocked Appointments</span>
            {Object.keys(blockedAppointmentsCount).map((key) => {
              return (
                <span
                  key={key}
                  className="row"
                  onClick={() => {
                    setSelectedRequest(undefined);
                    setNewOpen(false);
                    setBlockedAppointments(blockedAppointmentsCount[key]);
                    navigateToColumn(1);
                  }}
                >
                  {key}:&nbsp;<b>{blockedAppointmentsCount[key]?.length}</b>
                  <Icon src="eye" />
                </span>
              );
            })}
          </>
        )}
      </div>
    );
  }, [completeYears, completeStats, showFullStats, yearIndex]);

  const yearString = useMemo(() => {
    return CalendarUtils.format(moment().add(yearIndex, 'years').unix(), 'YYYY');
  }, [yearIndex]);

  return (
    <div className={className}>
      <ResponsiveColumns
        navigateTo={navigateTo}
        columns={[
          {
            name: 'Requests',
            element: (
              <div className="request-list full-height-panel underside-right-panel scroll-list">
                {!!onClose && (
                  <List>
                    <ListItem>
                      <DefaultButton name="Close Panel" onClick={onClose} />
                    </ListItem>
                    <ListItem>
                      <StaffBadge user={user} />
                    </ListItem>
                    {(permissions.includes(PERMISSIONS.super_admin) || user.id === ownId) && (
                      <>
                        <div className="year-row">
                          <span
                            className="span-button"
                            onClick={() => changeStatsYear(yearIndex - 1)}
                          >
                            {'<'}
                          </span>
                          <span className="year">{yearString}</span>
                          <span
                            className="span-button"
                            onClick={() => changeStatsYear(yearIndex + 1)}
                          >
                            {'>'}
                          </span>
                        </div>
                        {!showCalendar && !!statsElement && <ListItem>{statsElement}</ListItem>}
                      </>
                    )}
                  </List>
                )}
                <List>
                  {!approveMode && (
                    <>
                      <DefaultButton
                        name="New Shift Request"
                        onClick={startNewRequest}
                        className="blue"
                        style={{
                          width: '90%',
                          margin: '15px auto',
                          display: 'block',
                        }}
                        disabled={!completeStats.ok}
                      />
                      {!isAdmin && (
                        <DefaultButton
                          name="Handovers"
                          onClick={openHandovers}
                          style={{
                            width: '90%',
                            margin: '15px auto',
                            display: 'block',
                          }}
                          disabled={!completeStats.ok}
                        />
                      )}
                    </>
                  )}
                  {filteredReqs.map((request) => (
                    <SelectableListItem
                      key={request.id}
                      selected={request.id === (selectedRequest || {}).id}
                      onClick={() => {
                        setNonWorkingReason();
                        setNewOpen(true);
                        setSelectedRequest(request);
                        navigateToColumn(1);
                        if (onClickedItem && request.shifts.length > 0)
                          onClickedItem(request.shifts[0]);
                      }}
                      item={<VacationRequestBadge request={request} />}
                    />
                  ))}
                  {filteredReqs.length === 0 && (
                    <span className="padded-title">No items to show</span>
                  )}
                </List>
              </div>
            ),
          },
          !nonWorkingReason
            ? undefined
            : {
                name: 'Non-Working Days',
                element: (
                  <NonWorkingDays
                    key={nonWorkingReason + yearIndex + userId}
                    reason={NON_WORKING_MAP[nonWorkingReason]}
                    onClose={() => setNonWorkingReason()}
                    onClickedItem={onClickedItem}
                    userId={userId}
                    yearIndex={yearIndex}
                    requests={filteredReqs}
                  />
                ),
              },
          !blockedAppointments
            ? undefined
            : {
                name: 'Blocked Appointments',
                element: (
                  <BlockedAppointments
                    key={nonWorkingReason + yearIndex + userId}
                    onClose={() => setBlockedAppointments()}
                    onClickedItem={onClickedItem}
                    appointments={blockedAppointments}
                  />
                ),
              },
          !newOpen
            ? undefined
            : {
                name: 'Edit',
                element: (
                  <EditRequest
                    request={selectedRequest || newRequest}
                    setRequest={approveMode ? setSelectedRequest : setNewRequest}
                    onClose={() => {
                      setNewOpen(false);
                      setNewRequest(DEFAULT_REQUEST);
                      setSelectedRequest(undefined);
                      navigateToColumn(0);
                      if (!!setShowShifts) setShowShifts([]);
                    }}
                    allowEdit={!selectedRequest}
                    allowApprove={approveMode}
                    onClickedItem={onClickedItem}
                    getStats={getStats}
                    shifts={shifts}
                    shiftType={shiftType}
                    setShiftType={setShiftType}
                  />
                ),
              },
          !handovers
            ? undefined
            : {
                name: 'Handovers',
                element: <EditHandovers onClose={() => setHandovers(false)} />,
              },
          !showCalendar
            ? undefined
            : {
                name: 'Calendar',
                element: (
                  <ShiftSchedule
                    statsElement={statsElement}
                    onSelectSlot={onSelectSlot}
                    date={date}
                    setDate={setDate}
                    newOpen={newOpen}
                    selectedRequest={selectedRequest}
                    userId={userId}
                    extraShifts={extraShifts}
                    clinicId={clinic.id}
                    allVacations={true}
                  />
                ),
              },
        ]}
      />
    </div>
  );
};

export const ShiftSchedule = ({
  userId,
  extraShifts,
  statsElement,
  onSelectSlot,
  date,
  setDate,
  newOpen,
  selectedRequest,
  clinicId,
  allVacations,
}) => {
  const dispatch = useAppDispatch();
  const admins = useAppSelector((state) => state.admins.byId);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const shifts = useAppSelector((state) => state.shifts.shifts);
  const clinics = useAppSelector((state) => state.resource.clinics);

  useEffect(() => {
    let startDate = moment.unix(date).startOf('month').unix();
    let endDate = moment.unix(date).endOf('month').unix();
    dispatch(
      api.Shifts.getDayShifts({
        clinicId,
        date: startDate,
        endDate,
      }),
    );
  }, [dispatch, date, clinicId]);

  const shiftEvents = useMemo(() => {
    const directors = StaffUtils.getClinicDirectors(clinics);
    const FORMAT = 'DDMMYYYY';
    const otherVacations = {};
    const overrideTexts = {};
    let filtered = shifts.filter((shift) => {
      let physician = physicians[shift.user_id];
      if (
        allVacations &&
        shift.non_working &&
        !!physician &&
        userId !== physician.id &&
        PhysicianUtils.isNursePractitioner(physician) &&
        !directors.includes(physician.id)
      ) {
        const isVacation = shift.non_working_reason === NON_WORKING_MAP.vacation;
        const isStat = shift.non_working_reason === 'STAT';

        if (isVacation || isStat) {
          let dateString = CalendarUtils.format(shift.start, FORMAT);
          if (!otherVacations[dateString]) {
            otherVacations[dateString] = true;
            overrideTexts[shift.id] = isStat ? 'STAT' : 'Other NP Vacation';
            return true;
          }
        }
      }
      if (userId) return shift.user_id === userId;
      return true;
    });

    filtered = [...filtered, ...(extraShifts || [])];
    return StaffUtils.shiftPillsToEvents(filtered, physicians, admins, undefined, overrideTexts);
  }, [shifts, userId, admins, physicians, extraShifts, allVacations, clinics]);

  return (
    <div className="shift-calendar">
      {(!!statsElement && statsElement) || null}
      <CalendarMonthToolbar date={date} setDate={setDate} />
      <TimeZoneAgnosticBigCalendar
        key={shiftEvents.length}
        timeZoneName={CalendarUtils.getTargetTimezone()}
        events={shiftEvents}
        components={{
          event: ({ event }) => <Event event={event} />,
        }}
        view="month"
        defaultView="month"
        toolbar={false}
        startAccessor="start"
        endAccessor="end"
        defaultDate={CalendarUtils.momentToDate(date)}
        date={CalendarUtils.momentToDate(date)}
        onNavigate={(date) => {
          let converted = moment(date).unix();
          setDate(converted);
        }}
        onSelectEvent={(event) => {
          if (event && event.start) {
            onSelectSlot({
              action: 'click',
              slots: [convertDateToDateTime(event.start)],
            });
          }
        }}
        selectable={newOpen && !selectedRequest}
        onSelectSlot={onSelectSlot}
        style={{ width: '300px' }}
      />
    </div>
  );
};

const NonWorkingDays = ({ userId, reason, onClose, onClickedItem, yearIndex, requests }) => {
  const dispatch = useAppDispatch();
  const [shifts, setShifts] = useState();

  useEffect(() => {
    function getApprovedShifts(requests = [], yearIndex) {
      let targetYear = moment().add(yearIndex, 'years').format('YYYY');
      let approved = requests.filter(({ status }) => status.toLowerCase().includes('approved'));
      let approvedShifts = _.flatten(approved.map(({ shifts }) => shifts));
      return approvedShifts.filter(
        (shift) => !shift.rejected && moment.unix(shift.start).format('YYYY') === targetYear,
      );
    }

    if (reason !== NON_WORKING_MAP.approved) {
      dispatch(api.Shifts.getUserNonWorkingShifts({ userId, yearIndex })).then((shifts) => {
        if (shifts) {
          setShifts(shifts.filter(({ non_working_reason }) => non_working_reason === reason));
        }
      });
    } else {
      setShifts(getApprovedShifts(requests, yearIndex));
    }
  }, [dispatch, reason, userId, yearIndex, requests]);

  return (
    <div className="request-options underside-right-panel padded-box non-working">
      <div className="close">
        <Icon src="xmark" onClick={onClose} />
      </div>
      <span className="large-title">{reason} Days</span>
      <ProgressMessage show={!shifts} />
      <div className="stack-days">
        {!!shifts && (
          <>
            {(shifts || []).length === 0 && <span className="flex mt-3">No items to show</span>}
            <StackItemsView
              stack={shifts}
              describeItem={(shift) =>
                CalendarUtils.format(shift.start) +
                ' ' +
                ShiftUtils.getShiftDescription(shift) +
                (shift.notes ? ` - ${shift.notes}` : '')
              }
              onClickedItem={onClickedItem}
              hideStatus={true}
            />
          </>
        )}
      </div>
    </div>
  );
};

const BlockedAppointments = ({ onClose, onClickedItem, appointments }) => {
  return (
    <div className="request-options underside-right-panel padded-box blocked-appointments">
      <div className="close">
        <Icon src="xmark" onClick={onClose} />
      </div>
      <span className="large-title">Blocked Appointments</span>
      <ProgressMessage show={!appointments} />
      <div className="stack-days">
        {!!appointments && (
          <>
            {(appointments || []).length === 0 && (
              <span className="flex mt-3">No blocked appointments to show</span>
            )}
            <StackItemsView
              stack={appointments}
              describeItem={(appointment) => {
                let complaint = appointment.complaints || '';
                if (TIME_OFF_REASONS.some((str) => complaint.includes(str + ' - ')))
                  complaint = complaint.split(' - ')[1];
                return (
                  CalendarUtils.format(appointment.date) +
                  ' (' +
                  AppointmentUtils.getDuration(appointment) * 60 +
                  ' min) ' +
                  (complaint ? ` - ${complaint}` : '')
                );
              }}
              onClickedItem={onClickedItem}
              hideStatus={true}
            />
          </>
        )}
      </div>
    </div>
  );
};

const EditRequest = ({
  request,
  setRequest,
  onClose,
  allowEdit,
  allowApprove = false,
  onClickedItem,
  getStats,
  shifts,
  shiftType,
  setShiftType,
}) => {
  const dispatch = useAppDispatch();
  const admins = useAppSelector((state) => state.admins.byId);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const ownId = useAppSelector((state) => state.authentication.user.id);
  const [loadingButton, setLoadingButton] = useState();

  async function submitRequest() {
    setLoadingButton('submit');
    await dispatch(api.Shifts.addShiftRequest({ shiftRequest: request }));
    setLoadingButton();
    if (onClose) onClose();
  }

  async function saveShifts() {
    setLoadingButton('save');
    let response = await dispatch(api.Shifts.addRequestShifts({ requestId: request.id }));
    setLoadingButton();
    return onBatchAction(response);
  }

  async function revertShifts(originalShift) {
    let resultRequest;

    if (originalShift) {
      let replaced = getMatchingShift(request.replaced_shifts, originalShift);
      setLoadingButton(originalShift.start);
      if (replaced) {
        resultRequest = await dispatch(
          api.Shifts.revertRequestShifts({
            requestId: request.id,
            shiftId: replaced.id,
          }),
        );
      } else {
        let date = moment.unix(originalShift.start);
        let shift = (shifts || []).find((shift) => {
          let currentDate = moment.unix(shift.start);
          return shift.user_id === request.user_id && CalendarUtils.isSameDay(date, currentDate);
        });
        if (shift) {
          await dispatch(api.Shifts.deleteShift({ shiftId: shift.id }));
        }
      }

      let finalShifts = request.shifts.map((shift) => {
        if (CalendarUtils.isSameDay(moment.unix(originalShift.start), moment.unix(shift.start))) {
          return {
            ...shift,
            rejected: true,
          };
        } else {
          return shift;
        }
      });
      let totalEnabled = finalShifts.filter((shift) => !shift.rejected).length;
      let newRequest = {
        ...request,
        status:
          totalEnabled > 0
            ? SHIFT_REQUEST_STATUS.partially_approved
            : SHIFT_REQUEST_STATUS.declined,
        shifts: finalShifts,
      };
      resultRequest = await dispatch(api.Shifts.approveShiftRequest({ shiftRequest: newRequest }));
    } else {
      setLoadingButton('save');
      resultRequest = await dispatch(api.Shifts.revertRequestShifts({ requestId: request.id }));
      let newRequest = {
        ...request,
        status: SHIFT_REQUEST_STATUS.declined,
        shifts: request.shifts.map((shift) => {
          return {
            ...shift,
            rejected: true,
          };
        }),
      };
      resultRequest = await dispatch(api.Shifts.approveShiftRequest({ shiftRequest: newRequest }));
    }

    setLoadingButton();
    return onBatchAction(resultRequest);
  }

  async function onBatchAction(resultRequest) {
    if (resultRequest) {
      dispatch(api.Shifts.getClinicShifts({ clinicId: request.clinic_id }));
      if (!!getStats) {
        getStats(request.user_id);
      }
      setRequest(resultRequest);
    }
  }

  async function approveRequest(approved) {
    let status;

    if (approved) {
      let hasRejected = request.shifts.find((shift) => !!shift.rejected);
      status = hasRejected
        ? SHIFT_REQUEST_STATUS.partially_approved
        : SHIFT_REQUEST_STATUS.approved;
      setLoadingButton(SHIFT_REQUEST_STATUS.approved);
    } else {
      setLoadingButton(SHIFT_REQUEST_STATUS.declined);
      status = SHIFT_REQUEST_STATUS.declined;
    }

    let toSubmit = {
      ...request,
      status,
    };

    let result = await dispatch(api.Shifts.approveShiftRequest({ shiftRequest: toSubmit }));
    setLoadingButton();
    if (result) {
      setRequest(result);
      if (approved && !!getStats) {
        getStats(request.user_id);
      }
    }
  }

  function onDeleteShiftFromRequest(toDelete) {
    setRequest((prevState) => {
      return {
        ...prevState,
        shifts: prevState.shifts.filter((shift) => shift.start !== toDelete.start),
      };
    });
  }

  const reviewedByCurrentUser = !!((request || {}).reviewed_by || []).find((id) => id === ownId);

  if (reviewedByCurrentUser) allowEdit = false;

  const key = allowEdit + '-' + (request.id || 'none');

  const stackClassName = allowEdit ? 'stack-days edit' : 'stack-days no-edit';

  return (
    <div className="request-options underside-right-panel padded-box" key={key}>
      <div className="close">
        <Icon src="xmark" onClick={onClose} />
      </div>
      <span className="large-title">Shift Request</span>
      <br />
      {!allowEdit && (
        <>
          <span className="small-title">Status</span>
          <span>{request.status}</span>
        </>
      )}
      {allowEdit && (
        <DefaultSelectField
          label="Shift Type"
          width="250px"
          name="shift_type"
          value={shiftType}
          onChange={(event) => setShiftType(event.target.value)}
          inputElements={SHIFT_TYPES.map((option) => (
            <MenuItem id={'menu-item-' + option.label} value={option.value} key={option.label}>
              {option.label}
            </MenuItem>
          ))}
        />
      )}
      <span className="small-title" style={{ marginTop: '15px' }}>
        Days Requested
      </span>
      <div className={stackClassName}>
        {request.shifts.length === 0 && 'Click on the days you want to request'}
        {!allowApprove && request.status === SHIFT_REQUEST_STATUS.pending && (
          <StackItemsView
            stack={request.shifts}
            describeItem={(shift) =>
              `${CalendarUtils.format(shift.start)} ${ShiftUtils.getShiftDescription(shift)}`
            }
            onDeleteItem={allowEdit && !loadingButton ? onDeleteShiftFromRequest : null}
            onClickedItem={onClickedItem}
          />
        )}
        {(allowApprove || request.status !== SHIFT_REQUEST_STATUS.pending) && (
          <div className="stack">
            {request.shifts.map((shift, index) => {
              const checked = !shift.rejected;
              const revertShift = getMatchingShift(request.replaced_shifts, shift);
              const revertShiftDescription = revertShift
                ? `Revert to ${ShiftUtils.getShiftDescription(revertShift)} shift`
                : 'Remove requested shift';
              return (
                <div className="row">
                  <div className="stack-item">
                    <span onClick={onClickedItem ? () => onClickedItem(shift) : undefined}>
                      {moment.unix(shift.start).format('DD/MM/YYYY')}&nbsp;
                      {ShiftUtils.getShiftDescription(shift)}
                    </span>
                    <DefaultCheckbox
                      key={checked}
                      name="shift-approved"
                      value={checked}
                      disabled={!allowApprove || request.status !== SHIFT_REQUEST_STATUS.pending}
                      onChange={() => {
                        setRequest((prevState) => {
                          let shifts = prevState.shifts.map((shift, shiftIndex) => {
                            let rejected = shift.rejected;
                            if (index === shiftIndex) rejected = checked;
                            return {
                              ...shift,
                              rejected,
                            };
                          });
                          return {
                            ...prevState,
                            shifts,
                          };
                        });
                      }}
                    />
                  </div>
                  {allowApprove && !shift.rejected && !!request.replaced_shifts && (
                    <Icon
                      src="undo-alt"
                      alt={revertShiftDescription}
                      width="10px"
                      style={{ margin: '0 0 0 5px' }}
                      onClick={() => revertShifts(shift)}
                      showTooltip={true}
                      loading={loadingButton === shift.start}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <span className="small-title">Comments</span>
      <TextareaAutosize
        key={key}
        name="comments"
        value={request.comments || ''}
        disabled={!allowEdit || !!loadingButton}
        onChange={(event) => {
          let comments = event.target.value;
          setRequest((prevState) => {
            return {
              ...prevState,
              comments,
            };
          });
        }}
      />
      {(request.reply || allowApprove) && (
        <>
          <span className="small-title">Reply</span>
          <TextareaAutosize
            name="reply"
            value={request.reply}
            disabled={
              !allowApprove || !!loadingButton || request.status !== SHIFT_REQUEST_STATUS.pending
            }
            onChange={(event) => {
              let reply = event.target.value;
              setRequest((prevState) => {
                return {
                  ...prevState,
                  reply,
                };
              });
            }}
          />
        </>
      )}
      {allowApprove && request.reviewed_by.length > 0 && (
        <div className="button-wrapper row">
          {!request.replaced_shifts && (
            <ConfirmationButton
              name="Save Requests to Calendar"
              className="blue"
              onClick={saveShifts}
              style={{ margin: 'auto', width: '200px' }}
              loading={loadingButton === 'save'}
              confirmation={{
                message:
                  "This action will remove this user's other shifts for each of the days. Are you sure you want to proceed?",
              }}
            />
          )}
        </div>
      )}
      {allowApprove && !reviewedByCurrentUser && (
        <div className="button-wrapper row">
          <DefaultButton
            name="Decline"
            className="red"
            onClick={() => approveRequest(false)}
            style={{ margin: 'auto', width: '120px' }}
            disabled={loadingButton === SHIFT_REQUEST_STATUS.approved}
            loading={loadingButton === SHIFT_REQUEST_STATUS.declined}
          />
          <DefaultButton
            name="Approve"
            className="blue"
            onClick={() => approveRequest(true)}
            style={{ margin: 'auto', width: '120px' }}
            disabled={loadingButton === SHIFT_REQUEST_STATUS.declined}
            loading={loadingButton === SHIFT_REQUEST_STATUS.approved}
          />
        </div>
      )}
      {(request.reviewed_by || []).length > 0 && (
        <>
          <span className="small-title">Reviewed By</span>
          {(request.reviewed_by || []).map((id) => (
            <StaffBadge user={admins[id] || physicians[id]} />
          ))}
        </>
      )}
      {allowEdit && (
        <div className="button-wrapper row">
          <DefaultButton
            name="Submit"
            className="blue"
            onClick={submitRequest}
            disabled={(request.shifts || []).length === 0}
            style={{ margin: 'auto', width: '120px' }}
            loading={loadingButton === 'submit'}
          />
        </div>
      )}
    </div>
  );
};

function getMatchingShift(shifts = [], shift = {}) {
  const shiftDate = moment.unix(shift.start);
  return shifts.find((replaced) => {
    return CalendarUtils.isSameDay(moment.unix(replaced.start), shiftDate);
  });
}

const VacationRequestBadge = ({ request }) => {
  let length =
    request.status === SHIFT_REQUEST_STATUS.partially_approved
      ? `${request.shifts.filter((shift) => !shift.rejected).length}/${request.shifts.length}`
      : request.shifts.length;

  let title = `${length} days`;
  let firstLine = request.shifts
    .map((shift) => CalendarUtils.format(shift.start, 'DD/MM'))
    .join(', ');
  let secondLine = `Requested on ${CalendarUtils.format(request.date_requested)}`;
  let alertIcon = '';
  if (request.status === SHIFT_REQUEST_STATUS.approved) alertIcon = 'check-double';
  else if (request.status === SHIFT_REQUEST_STATUS.partially_approved) alertIcon = 'check';
  else if (request.status === SHIFT_REQUEST_STATUS.pending) alertIcon = 'hourglass';
  else if (request.status === SHIFT_REQUEST_STATUS.declined) alertIcon = 'times';

  let alertText = request.status;

  return (
    <ListBadge
      alertIcon={alertIcon}
      alert={alertText}
      title={title}
      firstLine={firstLine}
      secondLine={secondLine}
    />
  );
};
export default Vacations;
